<script lang="ts" setup>
import BasicButton from '~/components/form-elements/BasicButton.vue'
import RadioButtons from '~/components/form-elements/RadioButtons.vue'
import AddressSelect from '~/components/form-elements/select/AddressSelect.vue'
import CustomModalWindow from '~/components/modal-windows/CustomModalWindow.vue'
import type OrderShippingType from '~/enums/order-shipping-type'
import helpers from '~/helpers'
import { useCartStore } from '~/store/cart'
import type { RadioButtonChoice, SelectedItem } from '~/types/form-elements'

const model = defineModel<boolean>()

const props = defineProps<{
  declaredValueInKopecks: number
}>()

type Filter = {
  latitude: number
  longitude: number
  shippingTypeIds: OrderShippingType[]
  weightInGrams: number
}

const maxPickupPoints = 10
const messages = {
  failedToCalculateShippingCost: 'Не удалось рассчитать стоимость доставки.',
  failedToGetPickupPoints: 'Не удалось получить пункты выдачи заказов.'
}

const { $apiHelper, $toast } = useNuxtApp()
const cartStore = useCartStore()

const errorMessage = ref<string | null>(null)
const filter = ref<Filter | null>(null)
const isPickupPointsDataLoading = ref<boolean>(false)
const isShippingCostCalculating = ref<boolean>(false)
const pickupPointsData = ref<any[]>([])
const selectedAddress = ref<SelectedItem | null>(null)
const selectedPickupPoint = ref<any>(null)
const shippingCostInKopecks = ref<number | null>(null)

const pickupPointsChoices = computed(
  () => pickupPointsData
    .value
    .slice(0, maxPickupPoints)
    .map(
      (x: any): RadioButtonChoice => {
        const { fullAddress, latitude, longitude } = x.attributes
        const distance = calculateDistance(
          selectedAddress.value!.meta.latitude,
          selectedAddress.value!.meta.longitude,
          latitude,
          longitude
        );
        const distanceText = distance >= 1000
          ? `${(distance / 1000).toFixed(1).replace('.', ',')}\xA0км`
          : `${Math.round(distance)}\xA0м`

        return { description: `От\xA0вас в\xA0${distanceText}`, labelText: fullAddress, value: x }
      }
    )
)

watch(
  () => selectedPickupPoint.value,
  async (x: any): Promise<void> => {
    if (x) {
      await calculateShippingCost()
    }
  }
)

async function calculateShippingCost (): Promise<void> {
  isShippingCostCalculating.value = true

  const { declaredValueInKopecks } = props

  errorMessage.value = null
  shippingCostInKopecks.value = null

  const { externalId, shippingTypeId } = selectedPickupPoint.value!.attributes

  try {
    const { meta } = await $apiHelper.actions.getShippingCost(
      shippingTypeId,
      cartStore.weightInGrams as number,
      { declaredValueInKopecks, pickUpPointId: externalId }
    ) as any
    errorMessage.value = meta.errorMessage
    shippingCostInKopecks.value = meta.shippingCostInKopecks
  } catch (error) {
    console.error(error)

    $toast.error(messages.failedToCalculateShippingCost)
  }

  isShippingCostCalculating.value = false
}

function close (): void {
  model.value = false
}

function confirmSelection (): void {
  cartStore.shippingCostInKopecks = shippingCostInKopecks.value
  const { externalId, fullAddress } = selectedPickupPoint.value!.attributes
  cartStore.shippingData = { fullAddress, pickupPointId: externalId }

  close()
}

function calculateDistance (lat1: number, lon1: number, lat2: number, lon2: number): number {
  const r = 6371000
  const toRad = (angle: number): number => (angle * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(toRad(lat1))
    * Math.cos(toRad(lat2))
    * Math.sin(dLon / 2)
    * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return r * c;
}

async function getPickupPoints (): Promise<void> {
  isPickupPointsDataLoading.value = true

  try {
    const { latitude, longitude, shippingTypeIds } = filter.value!
    const params = {
      'filter[latitude]': latitude,
      'filter[longitude]': longitude,
      'filter[shippingTypeIds][]': shippingTypeIds[0]
    }
    const { data } = await $apiHelper.pickupPoints.getPickupPoints(params)
    pickupPointsData.value = data
  } catch (error) {
    console.error(error)

    $toast.error(messages.failedToGetPickupPoints)
  }

  isPickupPointsDataLoading.value = false
}

async function setAddress (x: SelectedItem): Promise<void> {
  if (!x) {
    selectedAddress.value = null
    selectedPickupPoint.value = null
    pickupPointsData.value = []
    shippingCostInKopecks.value = null
    cartStore.shippingCostInKopecks = null
    cartStore.shippingData = null

    return
  }

  console.log('setAddress', x)

  selectedAddress.value = x

  filter.value = {
    latitude: x.meta.latitude,
    longitude: x.meta.longitude,
    shippingTypeIds: [cartStore.orderShippingType!],
    weightInGrams: cartStore.weightInGrams!
  }

  console.log('filter.value', filter.value)

  await getPickupPoints()
}
</script>

<template>
  <CustomModalWindow v-model="model" size="large">
    <template #title>
      Выбор пункта выдачи заказов (ПВЗ)
    </template>

    <div class="min-h-80">
      <Notification is-small status="info">
        Укажите адрес&nbsp;— подберём ближайшие ПВЗ.
      </Notification>

      <AddressSelect class="mt-4" @set-address="setAddress" />

      <div v-if="selectedAddress" class="mt-6">
        <Preloader v-if="isPickupPointsDataLoading" />
        <template v-else-if="pickupPointsData.length">
          <RadioButtons
            v-model="selectedPickupPoint"
            :choices="pickupPointsChoices"
            is-required
            is-vertical
            label-text="ПВЗ"
            name="pickupPoint"
            with-blocks
          />
        </template>
        <Notification v-else status="info">
          Нет ближайших ПВЗ.
        </Notification>
      </div>

      <div class="mt-6">
        <Preloader v-if="isShippingCostCalculating" />
        <div v-else-if="shippingCostInKopecks !== null" class="text-xl">
          Стоимость доставки: {{ helpers.formatPrice(shippingCostInKopecks, true) }}&nbsp;₽
        </div>
      </div>
    </div>

    <BasicButton
      v-if="errorMessage === null && shippingCostInKopecks !== null"
      class="mt-6"
      color="green"
      title="Подтвердить выбор"
      @click="confirmSelection"
    />

    <template #action>
      <a href="#" @click.prevent="close">Закрыть</a>
    </template>
  </CustomModalWindow>
</template>
